import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Apple, Smartphone, Download, Globe, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import LogoHeader from "@/components/logo-header";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export default function InstallPage() {
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [browser, setBrowser] = useState<"safari" | "chrome" | "android">(
    () => {
      if (typeof navigator !== "undefined") {
        if (/android/i.test(navigator.userAgent)) return "android";
        if (/crios/i.test(navigator.userAgent)) return "chrome";
        if (/iPad|iPhone|iPod/.test(navigator.userAgent)) return "safari";
      }
      return "safari";
    },
  );

  useEffect(() => {
    const typedNavigator = navigator as Navigator & { standalone?: boolean };
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      typedNavigator.standalone === true;
    if (isStandalone) {
      navigate("/", { replace: true });
      return;
    }

    const typedWindow = window as Window & {
      deferredPrompt?: BeforeInstallPromptEvent;
    };
    if (typedWindow.deferredPrompt) {
      setDeferredPrompt(typedWindow.deferredPrompt);
    }

    const handlePromptAvailable = () => {
      if (typedWindow.deferredPrompt) {
        setDeferredPrompt(typedWindow.deferredPrompt);
      }
    };

    typedWindow.addEventListener(
      "deferredpromptavailable",
      handlePromptAvailable,
    );
    return () =>
      typedWindow.removeEventListener(
        "deferredpromptavailable",
        handlePromptAvailable,
      );
  }, [navigate]);

  const handleNativeInstall = async () => {
    if (!deferredPrompt) {
      alert(
        "Native installation is not supported by your current browser. Please follow the manual step-by-step instructions below.",
      );
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      setDeferredPrompt(null);
    }
  };

  return (
    <>
      <div className="sticky top-0 flex justify-center w-full z-[99] bg-white p-6 shadow-[0_8px_16px_-4px_rgba(0,0,0,0.1)]">
        <Link to="/" className="flex items-center gap-2">
          <LogoHeader className="max-w-[200px]" />
        </Link>
      </div>
      <div className="min-h-[100dvh] bg-white p-6 flex flex-col max-w-md mx-auto relative pb-12">
        <div className="absolute top-0 left-0 right-0 h-64 bg-gradient-to-b from-gray-50 to-white -z-10" />
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-3 tracking-tight">
            Install the App
          </h1>
          <p className="text-gray-500 text-sm px-4 leading-relaxed">
            Add ProjectSG Dashboard to your home screen for quick access and the
            best experience.
          </p>
        </div>

        <div className="mb-6">
          <button
            onClick={handleNativeInstall}
            className="flex items-center justify-center gap-2 w-full py-4 bg-black text-white rounded-2xl font-bold shadow-lg hover:bg-gray-900 active:scale-[0.98] transition-all"
          >
            <Download size={20} />
            Install to Home Screen
          </button>
          <p className="text-gray-500 text-center text-xs px-4 mt-3">
            1-Click Install for supported devices
          </p>
        </div>

        <div className="relative mb-6">
          <div
            className="absolute inset-0 flex items-center"
            aria-hidden="true"
          >
            <div className="w-full border-t border-gray-200" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-3 text-sm text-gray-400 font-medium tracking-wide uppercase">
              or install manually
            </span>
          </div>
        </div>

        <div className="flex p-1 bg-gray-100 rounded-2xl mb-8 shrink-0">
          <button
            className={`flex-1 flex flex-col items-center justify-center gap-1 py-2 rounded-xl text-xs font-bold transition-all ${browser === "safari" ? "bg-white text-black shadow-sm" : "text-gray-500 hover:text-black"}`}
            onClick={() => setBrowser("safari")}
          >
            <Apple size={18} />
            Safari
          </button>
          <button
            className={`flex-1 flex flex-col items-center justify-center gap-1 py-2 rounded-xl text-xs font-bold transition-all ${browser === "chrome" ? "bg-white text-black shadow-sm" : "text-gray-500 hover:text-black"}`}
            onClick={() => setBrowser("chrome")}
          >
            <Globe size={18} />
            Chrome (iOS)
          </button>
          <button
            className={`flex-1 flex flex-col items-center justify-center gap-1 py-2 rounded-xl text-xs font-bold transition-all ${browser === "android" ? "bg-white text-black shadow-sm" : "text-gray-500 hover:text-black"}`}
            onClick={() => setBrowser("android")}
          >
            <Smartphone size={18} />
            Android
          </button>
        </div>

        <div className="flex-1 mb-6">
          {browser === "chrome" && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm relative overflow-hidden">
                <h3 className="font-bold text-gray-900 mb-4">
                  Install using Chrome
                </h3>
                <div className="flex gap-4 items-start mb-4">
                  <div className="w-6 h-6 rounded-full bg-black text-white flex items-center justify-center text-xs font-bold shrink-0">
                    1
                  </div>
                  <p className="text-sm text-gray-700">
                    Click <strong>bottom right share button</strong>
                  </p>
                </div>
                <div className="flex gap-4 items-start mb-4">
                  <div className="w-6 h-6 rounded-full bg-black text-white flex items-center justify-center text-xs font-bold shrink-0">
                    2
                  </div>
                  <p className="text-sm text-gray-700">
                    Click <strong>View More</strong>
                  </p>
                </div>
                <div className="flex gap-4 items-start mb-6">
                  <div className="w-6 h-6 rounded-full bg-black text-white flex items-center justify-center text-xs font-bold shrink-0">
                    3
                  </div>
                  <p className="text-sm text-gray-700">
                    Click <strong>Add to Home Screen</strong>
                  </p>
                </div>

                <div className="space-y-4 mt-6">
                  <img
                    src="/install-step-2.png"
                    alt="View More"
                    className="w-[75%] max-w-[260px] mx-auto block rounded-xl shadow-md border border-gray-200"
                  />
                  <img
                    src="/install-step-3.png"
                    alt="Add to Home Screen"
                    className="w-[75%] max-w-[260px] mx-auto block rounded-xl shadow-md border border-gray-200"
                  />
                </div>
              </div>
            </div>
          )}

          {browser === "safari" && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm relative overflow-hidden">
                <h3 className="font-bold text-gray-900 mb-4">
                  Install using Safari
                </h3>
                <div className="flex gap-4 items-start mb-4">
                  <div className="w-6 h-6 rounded-full bg-black text-white flex items-center justify-center text-xs font-bold shrink-0">
                    1
                  </div>
                  <p className="text-sm text-gray-700">
                    Click the <strong>three dots (...)</strong> in the bottom
                    right corner
                  </p>
                </div>
                <div className="flex gap-4 items-start mb-4">
                  <div className="w-6 h-6 rounded-full bg-black text-white flex items-center justify-center text-xs font-bold shrink-0">
                    2
                  </div>
                  <p className="text-sm text-gray-700">
                    Click <strong>Share</strong>
                  </p>
                </div>
                <div className="flex gap-4 items-start mb-4">
                  <div className="w-6 h-6 rounded-full bg-black text-white flex items-center justify-center text-xs font-bold shrink-0">
                    3
                  </div>
                  <p className="text-sm text-gray-700">
                    Click <strong>View More</strong>
                  </p>
                </div>
                <div className="flex gap-4 items-start mb-6">
                  <div className="w-6 h-6 rounded-full bg-black text-white flex items-center justify-center text-xs font-bold shrink-0">
                    4
                  </div>
                  <p className="text-sm text-gray-700">
                    Click <strong>Add to Home Screen</strong>
                  </p>
                </div>

                <div className="space-y-4 mt-6">
                  <img
                    src="/install-step-2.png"
                    alt="View More"
                    className="w-[75%] max-w-[260px] mx-auto block rounded-xl shadow-md border border-gray-200"
                  />
                  <img
                    src="/install-step-3.png"
                    alt="Add to Home Screen"
                    className="w-[75%] max-w-[260px] mx-auto block rounded-xl shadow-md border border-gray-200"
                  />
                </div>
              </div>
            </div>
          )}

          {browser === "android" && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm relative overflow-hidden">
                <h3 className="font-bold text-gray-900 mb-4">
                  Install using Android
                </h3>
                <div className="flex gap-4 items-start mb-4">
                  <div className="w-6 h-6 rounded-full bg-black text-white flex items-center justify-center text-xs font-bold shrink-0">
                    1
                  </div>
                  <p className="text-sm text-gray-700">
                    Tap the <strong>three dots</strong> menu icon at the top
                    right.
                  </p>
                </div>
                <div className="flex gap-4 items-start mb-6">
                  <div className="w-6 h-6 rounded-full bg-black text-white flex items-center justify-center text-xs font-bold shrink-0">
                    2
                  </div>
                  <p className="text-sm text-gray-700">
                    Find and tap <strong>Add to Home screen</strong> from the
                    list.
                  </p>
                </div>

                <div className="space-y-4 mt-6">
                  <img
                    src="/android-step-1.png"
                    alt="Tap Menu"
                    className="w-[75%] max-w-[260px] mx-auto block rounded-xl shadow-md border border-gray-200"
                  />
                  <img
                    src="/android-step-2.png"
                    alt="Add to Home Screen"
                    className="w-[75%] max-w-[260px] mx-auto block rounded-xl shadow-md border border-gray-200"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 mb-8">
          <div className="flex gap-3 mb-3">
            <Info className="text-blue-500 shrink-0 mt-0.5" size={20} />
            <h3 className="font-bold text-blue-900 text-sm">
              Important for Notifications
            </h3>
          </div>
          <p className="text-sm text-blue-800 ml-8 leading-relaxed mb-4">
            You <strong>must open the app</strong> from your home screen at
            least once after downloading it to enable notifications.
          </p>
        </div>

        <div className="mt-auto">
          <Link
            to="/"
            className="block w-full py-4 bg-gray-100 text-gray-900 text-center rounded-2xl font-bold shadow-sm hover:bg-gray-200 transition-colors"
          >
            Continue to Web App
          </Link>
        </div>
      </div>
    </>
  );
}
